import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";

// import JSZip from "jszip";
import axiosInstance from "../../utils/axiosInstance";

/**
 * THE SHIP ACKNOWLEDGEMENT, AS ONE SPELLING (W4-F2.12, Addendum 6 C, backend
 * B2.12).
 *
 * WHAT IT IS. `ship_acknowledged: true` tells `CapsuleUserAPIController` to
 * stamp `capsule_users.ship_acknowledged_at` on the CALLER'S OWN row, once,
 * the first time it is sent. That column is the durable half of Addendum 5 rule
 * 4: the per learner, per level record that this learner walked through Ship
 * themselves, which until now existed only in the client's ending ledger and
 * therefore only on one browser. Addendum 5 disclosed that cost and queued this
 * as the fix.
 *
 * IT IS NOT A NEW REQUEST, AND THAT IS THE POINT. Every path that fires a ship
 * event already posts to `/capsule-users` in the same tail, so the
 * acknowledgement is a field on a write the platform was already making: one
 * request, not two, and it can never half happen relative to the stamp it
 * travels with. The three tails are the legacy upload (the thunk below), the
 * versioned cohort's park release (`stampDeliverableSubmitted`), and the
 * already submitted Next path (`SubmitLevelDeliverable.jsx`).
 *
 * OWN ROW ONLY, AND THE CLIENT DOES NOT HAVE TO BE TRUSTED FOR THAT.
 * `POST /capsule-users` resolves the owner from the authenticated principal and
 * ignores the `user_id` in the body; the update route refuses a stamp on
 * another learner's row with a 403 and an unresolvable owner or capsule with a
 * 422. So a staff or impersonation view that reached one of these tails would
 * stamp its own row for that capsule and never the learner's.
 *
 * AND IT IS STILL NOT SENT BY A VIEWER THE SERVER REFUSED (W4-F2.12 fix batch,
 * Codex MAJOR 1). Harmless by the server's construction is not the same as not
 * attempted: F2.11 ruled that a viewer answered 401 or 403 on `/me/completion`
 * writes nothing, and "nothing" has to include this. The eligibility is decided
 * once in `Dashboard.jsx` (`shipAcknowledgementEligible`) and threaded to all
 * three tails, because every one of them makes its request before the guard
 * that used to be the whole of the rule.
 *
 * IDEMPOTENT, so a second send is a no-op: the server's update is guarded by
 * `whereNull('ship_acknowledged_at')` and keeps the FIRST acknowledgement. A
 * learner who uploads twice, or whose tail retries, does not move their own
 * record forward.
 *
 * A CONSTANT AND NOT THREE LITERALS. Three spellings of one contract field is
 * three chances for one of them to be renamed alone, and the failure that would
 * produce is silent on that path: the ship still plays, the ledger still
 * records, and only a learner arriving on a second device finds themselves
 * back at a Ship step they walked weeks ago.
 */
export const SHIP_ACKNOWLEDGEMENT = Object.freeze({ ship_acknowledged: true });

export const completeReadChallenge = createAsyncThunk(
  "dashboard/completeReadChallenge",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance("/cohort-users", "post", data);
      return response.data.data;
    } catch (error) {
      throw rejectWithValue(error.response.data.message);
    }
  },
);

export const fetchProgramData = createAsyncThunk(
  "dashboard/fetchProgramData",
  async (programId) => {
    const response = await axiosInstance(
      `/programs/${programId}?include=capsules.modules.lessons.quiz.questions,capsules.modules.lessons.userLesson`,
      "get",
    );
    return response.data.data;
  },
);

export const fetchMilestones = createAsyncThunk(
  "dashboard/fetchMilestones",
  async (cohortID) => {
    // G.2c: with_deliverable_meta=1 opts in to milestone.deliverable_meta
    // on this serialization path (MilestoneResource reads the request
    // flag; without it the key is omitted entirely). The plan-step
    // reference dock consumes it; everything else on the payload is
    // unchanged.
    const response = await axiosInstance(
      `/cohorts/${cohortID}?include=program.capsules.milestones&with_deliverable_meta=1`,
      "get",
    );
    return response.data.data;
  },
);

export const fetchQuizAnswers = createAsyncThunk(
  "dashboard/fetchQuizAnswers",
  async (data) => {
    const response = await axiosInstance("/quiz-answers", "post", data);
    return response.data.data;
  },
);

export const fetchCompleteLesson = createAsyncThunk(
  "dashboard/fetchCompleteLesson",
  async (data) => {
    const { lessonId, total_watch_time_seconds, replay_count } =
      typeof data === "object" ? data : { lessonId: data };
    const response = await axiosInstance(`/user-lessons/${lessonId}`, "put", {
      seek_time: 0,
      is_completed: true,
      total_watch_time_seconds: total_watch_time_seconds ?? 0,
      replay_count: replay_count ?? 0,
    });
    return response.data.data;
  },
);

// Capture Gap Closure F1: record ONE replay of an already completed lesson.
//
// The body is exactly `{ replay_increment: 1 }` and nothing else. It never
// sends is_completed, seek_time or watch time, so the local-only re-watch
// contract for completion and next-lesson writes is untouched and
// UserLessonObserver::saved() - which awards XP only on isDirty('is_completed')
// - awards nothing. The backend applies the increment atomically and refuses a
// non-empty replay_count in the same request (capture_gap_contract_v1.json).
//
// Fire-and-forget with NO automatic retry: a lost replay tick is a lost
// signal, and a retry on a monotonic counter is a way to invent one.
export const fetchRecordLessonReplay = createAsyncThunk(
  "dashboard/fetchRecordLessonReplay",
  async ({ userLessonId }) => {
    const response = await axiosInstance(
      `/user-lessons/${userLessonId}`,
      "put",
      { replay_increment: 1 },
    );
    return response.data.data;
  },
);

export const fetchUpdateNextLesson = createAsyncThunk(
  "dashboard/fetchUpdateNextLesson",
  async (data) => {
    const response = await axiosInstance(`/user-lessons`, "post", data);
    return response.data.data;
  },
);

export const fetchReflection = createAsyncThunk(
  "dashboard/fetchReflection",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance("/reflections", "post", data);
      return response.data.data;
    } catch (error) {
      throw rejectWithValue(error.response.data.message);
    }
  },
);

export const fetchUpdateReflection = createAsyncThunk(
  "dashboard/fetchUpdateReflection",
  async (data, { rejectWithValue }) => {
    try {
      const response = await axiosInstance(
        `/reflections/${data.id}`,
        "put",
        data,
      );
      return response.data.data;
    } catch (error) {
      throw rejectWithValue(error.response.data.message);
    }
  },
);

export const fetchMilestoneFeedbacks = createAsyncThunk(
  "dashboard/fetchMilestoneFeedbacks",
  async () => {
    const response = await axiosInstance("/milestone-responses", "get");
    return response.data.data;
  },
);

export const fetchSubmitMilestoneFeedback = createAsyncThunk(
  "dashboard/fetchSubmitMilestoneFeedback",
  async (data) => {
    try {
      const updateCapsuleUserData = {
        capsule_id: data.capsule_id,
        user_id: data.user_id,
        milestone_report_submitted: 1,
      };

      for (const report of data.milestone_report) {
        // Per-question timing fields (attached by SubmitLevelProgressModal)
        const timingFields = {
          started_at: report.started_at ?? null,
          time_spent_seconds: report.time_spent_seconds ?? null,
        };

        switch (report.type) {
          case "textarea":
            await axiosInstance("/milestone-responses", "post", {
              question_key: report.question_key,
              user_id: data.user_id,
              capsule_id: data.capsule_id,
              squad_id: data.squad_id,
              response_numeric: null,
              response_text: report.value,
              response_data: null,
              ...timingFields,
            });
            break;

          case "range":
            await axiosInstance("/milestone-responses", "post", {
              question_key: report.question_key,
              user_id: data.user_id,
              capsule_id: data.capsule_id,
              squad_id: data.squad_id,
              response_numeric: Number(report.value),
              response_text: null,
              response_data: null,
              ...timingFields,
            });
            break;

          case "select":
            await axiosInstance("/milestone-responses", "post", {
              question_key: report.question_key,
              user_id: data.user_id,
              capsule_id: data.capsule_id,
              squad_id: data.squad_id,
              response_numeric: Number(report.value),
              response_text: null,
              response_data: null,
              ...timingFields,
            });
            break;

          case "multiselect":
            await axiosInstance("/milestone-responses", "post", {
              question_key: report.question_key,
              user_id: data.user_id,
              capsule_id: data.capsule_id,
              squad_id: data.squad_id,
              response_numeric: null,
              response_text: null,
              response_data: { selected_values: report.value },
              ...timingFields,
            });
            break;

          case "squad_rating":
            await axiosInstance("/milestone-responses", "post", {
              question_key: report.question_key,
              user_id: data.user_id,
              capsule_id: data.capsule_id,
              squad_id: data.squad_id,
              response_numeric: null,
              response_text: null,
              response_data: {
                user_ratings: report.value,
              },
              ...timingFields,
            });
            break;

          case "peer_rating":
            // New Q-Matrix v2 peer rating type
            // Stores ratings for all squad members on one dimension
            await axiosInstance("/milestone-responses", "post", {
              question_key: report.question_key,
              user_id: data.user_id,
              capsule_id: data.capsule_id,
              squad_id: data.squad_id,
              response_numeric: null,
              response_text: null,
              response_data: report.value, // { ratings: [{ user_id, rating }, ...] }
              ...timingFields,
            });
            break;

          default:
            console.warn(`Unknown report type: ${report.type}`);
            break;
        }
      }

      const response = await axiosInstance(
        "/capsule-users",
        "post",
        updateCapsuleUserData,
      );
      return response.data.data;
    } catch (error) {
      console.error("Error submitting milestone feedback:", error);
      throw error;
    }
  },
);

/**
 * W4-F1 (technical brief 5.3 and 7, ruling S7): THE STAMP, ON ITS OWN.
 *
 * On a versioned cohort the deliverable stamp is not an upload-time fact. It is
 * what the platform writes when the output is IN, and the submission boundary
 * is where the client says so. This is the same one-line POST the upload thunk
 * has always made, lifted out so the release path can make it at the moment it
 * is true rather than twenty minutes early.
 */
export const stampDeliverableSubmitted = createAsyncThunk(
  "dashboard/stampDeliverableSubmitted",
  async ({ capsule_id, user_id }, { rejectWithValue }) => {
    try {
      const response = await axiosInstance("/capsule-users", "post", {
        capsule_id,
        user_id,
        deliverables_submitted: 1,
        /* W4-F2.12, Addendum 6 C: AND THE SHIP ACKNOWLEDGEMENT RIDES IT.
           See `SHIP_ACKNOWLEDGEMENT` at the head of this file. This thunk has
           exactly one caller, the park release in `Dashboard.jsx`, and that
           release IS the ship event on a deferred boundary: it is the line
           that calls `fireShipComplete`. */
        ...SHIP_ACKNOWLEDGEMENT,
      });
      return response.data.data;
    } catch (error) {
      /* THE DELIVERABLE STAMP IS THE SERVER'S ANYWAY. `FloorTransition::apply`
         stamps every member of the level's roster inside the transaction that
         emits the floor message, so losing that half here costs the local row a
         refresh and costs the platform nothing. It is reported rather than
         thrown so the ending it follows is never taken down by it.

         W4-F2.12: THE ACKNOWLEDGEMENT HALF IS NOT THE SERVER'S, AND THIS
         SENTENCE USED TO CLAIM THE WHOLE BODY COST NOTHING. Nothing else ever
         writes `ship_acknowledged_at` for this learner: the roster stamp is
         about the SQUAD'S output and this is about one learner's own
         walkthrough, and the level is closed behind them, so there is no
         second press that would retry it. A failure here therefore leaves this
         learner on the client ledger alone for this level, which is exactly
         the F2.10 state Addendum 5 disclosed, on one level instead of all of
         them.

         SO IT IS LOGGED RATHER THAN SWALLOWED. Degrading to the ledger is the
         right behaviour and a moment must never be taken down by a failed
         write, but a durable record silently not being written is the failure
         class this repo keeps paying for. The warning is what lets support
         tell "this learner was offered Ship twice" from "this learner never
         acknowledged". */
      // eslint-disable-next-line no-console
      console.warn(
        "completion: the ship acknowledgement write failed; this learner's walkthrough for this level is on the client ledger only",
        { capsule_id, user_id },
      );
      return rejectWithValue(error.response?.data || "Stamp failed");
    }
  },
);

/**
 * W4-F2.6 DELTA FIX BATCH, item 2 (Codex delta review NEW MAJOR): THE UPLOAD'S
 * TRANSPORT DEADLINE.
 *
 * The upload call passed `{}` as its config, so the instance had no `timeout`
 * and no `signal` and its promise could settle NEVER: a response lost behind a
 * stalled proxy after the server had already committed the attempt and
 * broadcast its result left `uploadOutstanding` true for the life of the page,
 * and the drawer painted `submitting` over an authoritative result it was
 * actively suppressing. The `timeout` promotion in `axiosInstance` has existed
 * since W4-F2.3 and was opt-in with, in its own words, no caller. This is the
 * caller.
 *
 * THE ARITHMETIC, AND IT IS DELIBERATELY GENEROUS. `validateFile` caps a single
 * file at 100 MB (video deliverables). 100 MB is 800 Mbit, and at a
 * conservative sustained 1 Mbit/s school uplink that is 800 seconds of
 * transfer before the server has read a byte of it. 100 seconds of headroom for
 * the request the server then has to answer gives 900 seconds.
 *
 * AND FIRING EARLY IS RECOVERABLE, WHICH IS WHY 900 AND NOT 3600. A timeout
 * here is not treated as a refusal: the caller re-reads the authenticated
 * economy read and takes its answer, so a deadline that fires on an upload the
 * server did in fact accept still paints the real result. The cost of too
 * short is one extra read; the cost of no deadline at all is the permanent
 * mask above.
 */
export const UPLOAD_TIMEOUT_MS = 900000;

/**
 * THE CODE THAT MEANS "NO ANSWER", WHICH IS NOT "REFUSED".
 *
 * Shaped like Laravel's own `ResponseUtil::makeError` body so `apiErrorCode`
 * reads it through the one contract every other refusal on this path uses.
 * There is no server behind it and there is deliberately no `message`: a
 * sentence invented here would be shown for a rejection the server never made.
 */
export const UPLOAD_NO_RESPONSE_CODE = "upload_no_response";

/**
 * A REJECTION THE TRANSPORT PRODUCED, rather than one the server sent.
 *
 * `ECONNABORTED` is what axios sets when its own `timeout` fires and when the
 * request is aborted; `ETIMEDOUT` is the node-side spelling. Both are read
 * ONLY when there is no response at all, so a real 408 or 504 from the server
 * stays on the ordinary refusal path with its own body.
 */
const isUnanswered = (error) =>
  !error?.response &&
  (error?.code === "ECONNABORTED" || error?.code === "ETIMEDOUT");

/**
 * Batch A, D4.4: A LEGACY REJECTION, TAGGED WITH THE WRITE THAT FAILED AND THE
 * HTTP STATUS. An object body is spread so `data.code` and `message` keep
 * their positions for every existing reader; a string or absent body becomes
 * the `message`. `submitPhase` is "upload" or "stamp"; `httpStatus` is 0 when
 * there was no response at all.
 */
const tagRejection = (error, submitPhase) => {
  const body = error?.response?.data;
  const httpStatus = error?.response?.status ?? 0;
  if (body && typeof body === "object" && !Array.isArray(body)) {
    return { ...body, submitPhase, httpStatus };
  }
  return {
    success: false,
    message: body || "Upload failed",
    submitPhase,
    httpStatus,
  };
};

export const fetchSubmitLevelDeliverable = createAsyncThunk(
  "dashboard/fetchSubmitLevelDeliverable",
  async (data, { rejectWithValue }) => {
    const {
      formData,
      capsule_id,
      user_id,
      onUploadProgress,
      /**
       * W4-F1: WHETHER THE UPLOAD ALSO STAMPS (ruling S7, the submission
       * boundary).
       *
       * True everywhere it has ever been true, which is every legacy cohort.
       * FALSE ON A VERSIONED COHORT, where the upload is the START of
       * submitting: the platform stamps the whole roster itself when the floor
       * result says the output is in, and `CapsuleUserAPIController` refuses an
       * early stamp on a `scored` level anyway. Posting it here was a write the
       * server was already throwing away.
       */
      stampCapsuleUser = true,
      /**
       * W4-F2.12 FIX BATCH (Codex MAJOR 1): WHETHER THE ACKNOWLEDGEMENT RIDES
       * THAT STAMP.
       *
       * A SECOND PARAMETER AND NOT A CONDITION ON THE FIRST, because they are
       * two different facts about two different subjects. `stampCapsuleUser`
       * asks whether the SQUAD'S output is in yet; this asks whether THIS
       * VIEWER is the learner acting for themselves. A legacy cohort viewed by
       * someone the completion read refused stamps and does not acknowledge,
       * and the two conditions cross.
       *
       * Defaulted `true` for the reason the drawer's prop is: a forgotten
       * `false` is a durable record silently never written.
       */
      acknowledgeShip = true,
      /**
       * W4-F2.7, F50b: THE ATTEMPT THE SERVER ASSIGNED TO THIS UPLOAD.
       *
       * B2.10 echoes `economy_attempt` on every entry of the upload's 200. It
       * is handed back through a CALLBACK rather than through the fulfilled
       * payload, and that is not a stylistic choice: three reducers and the
       * completion boundary read this thunk's payload, one of them deciding
       * whether to stamp the capsule-user row on the strength of it being
       * null. Putting an object where a null was would flip that decision for
       * every versioned scored upload.
       *
       * The caller takes it synchronously, before the await returns, exactly
       * as `onUploadProgress` takes its percentages.
       */
      onEconomyAttempt,
      /**
       * FE3: THE WRITE ITSELF, WHEN IT IS NOT THE LEGACY UPLOAD.
       *
       * The workspace capture commit (FE3 plan section 3 item 2) is the same
       * submission boundary with a different first write. Passing it here
       * rather than building a second thunk keeps EVERYTHING after that write
       * identical: the capsule-user stamp and its `acknowledgeShip` field
       * under the same `stampCapsuleUser` condition, and the fulfilled and
       * rejected actions three reducers already listen to (the capsule-user
       * merge, the projection's stale mark). Absent on every legacy caller,
       * which therefore takes the upload branch below unchanged.
       *
       * It resolves to the commit receipt, whose `attempt` is reported through
       * `onEconomyAttempt` exactly as the upload's echoed attempt is. A
       * rejection is carried as `{ captureError }`: the normalized workspace
       * error, which the capture caller maps (plan 4.6).
       */
      write,
    } = data;

    const updateCapsuleUserData = {
      capsule_id,
      user_id,
      deliverables_submitted: 1,
      /* W4-F2.12, Addendum 6 C: AND THE SHIP ACKNOWLEDGEMENT RIDES IT.
         See `SHIP_ACKNOWLEDGEMENT` at the head of this file. This body is
         posted only when `stampCapsuleUser` is true, which is every LEGACY
         cohort and no versioned one; on a legacy cohort the upload IS the ship
         event and the drawer emits it the moment this thunk resolves. The
         versioned cohort's acknowledgement rides `stampDeliverableSubmitted`
         above, at the park release, because that is where its ship event is.

         W4-F2.12 FIX BATCH: AND ONLY FOR A VIEWER WHO MAY ACKNOWLEDGE. This
         POST is made inside this thunk, so it is already out by the time the
         drawer emits and the dashboard's refusal guard reads the event. */
      ...(acknowledgeShip ? SHIP_ACKNOWLEDGEMENT : {}),
    };
    if (typeof write === "function") {
      try {
        const receipt = await write();
        if (typeof onEconomyAttempt === "function") {
          const attempt = Number(receipt?.attempt);
          onEconomyAttempt(
            receipt?.attempt !== null &&
              receipt?.attempt !== undefined &&
              Number.isFinite(attempt)
              ? attempt
              : null,
          );
        }
      } catch (error) {
        return rejectWithValue({ captureError: error ?? null });
      }
      if (!stampCapsuleUser) return null;
      try {
        const response = await axiosInstance(
          "/capsule-users",
          "post",
          updateCapsuleUserData,
        );
        return response.data.data;
      } catch (error) {
        return rejectWithValue({
          captureError: null,
          stampError: error.response?.data || "Upload failed",
        });
      }
    }
    /* Batch A, D4.4: THE UPLOAD AND THE STAMP ARE TWO WRITES, AND A
       REJECTION NOW SAYS WHICH ONE FAILED. They used to share one `try`, so a
       stamp that failed AFTER the upload was saved rejected exactly like an
       upload the server refused, and the drawer could not tell "nothing was
       submitted" from "it went in". The HTTP status is kept for the same
       reason. The unanswered branch is unchanged in both. */
    let uploaded;
    try {
      uploaded = await axiosInstance(
        "/deliverables/upload",
        "post",
        formData,
        { timeout: UPLOAD_TIMEOUT_MS },
        onUploadProgress,
      );
    } catch (error) {
      // Reject with the RAW response body, the same contract the quest thunks
      // use. The upload endpoint answers a post-reveal attempt with a typed
      // 409 (`data.code`), and the caller can only branch on that code if the
      // body survives the rejection. Previously the whole axios error object
      // was thrown through, putting a non-serializable value in the store and
      // burying the code two levels deeper than any caller looked.
      /* W4-F2.6 delta item 2: NO ANSWER IS ITS OWN ANSWER, and it is typed so
         the caller can tell it from a refusal. Without this branch a timeout
         arrives as the bare string fallback, indistinguishable from a network
         error the server never saw, and the drawer would tell a learner their
         upload failed at exactly the moment it may well have succeeded. */
      if (isUnanswered(error)) {
        return rejectWithValue({
          success: false,
          data: { code: UPLOAD_NO_RESPONSE_CODE },
        });
      }
      return rejectWithValue(tagRejection(error, "upload"));
    }
    /**
     * W4-F2.7, F50b: READ DEFENSIVELY, BECAUSE THE SERVER THAT SERVES IT IS
     * NOT DEPLOYED YET.
     *
     * The body is `{ success, data: [entry, ...] }` and every entry carries
     * the same `economy_attempt` (one upload is one attempt), null outside
     * an economy scope. A server without B2.10 carries the key on no entry,
     * and `null` is then what the caller is told, which is exactly what it
     * is told for a legacy upload: no attempt of ours to key anything on.
     *
     * THE MAXIMUM, NOT THE FIRST. One request can persist several rows and a
     * server that ever answered them from two attempts would be a contract
     * break; taking the highest means such a break cannot quietly lower this
     * client's mask below an attempt the server really assigned.
     */
    if (typeof onEconomyAttempt === "function") {
      const entries = Array.isArray(uploaded?.data?.data)
        ? uploaded.data.data
        : [];
      const attempts = entries
        .map((entry) => Number(entry?.economy_attempt))
        .filter((value) => Number.isFinite(value));
      onEconomyAttempt(attempts.length > 0 ? Math.max(...attempts) : null);
    }
    if (!stampCapsuleUser) {
      /* NULL AND NOT AN INVENTED ROW. Every reducer that consumes this
         fulfilled action already guards on `action.payload?.id`, so a null
         writes nothing rather than writing a guess. */
      return null;
    }
    try {
      const response = await axiosInstance(
        "/capsule-users",
        "post",
        updateCapsuleUserData,
      );
      return response.data.data;
    } catch (error) {
      /* The upload above was saved. Only the stamp failed, so the outcome is
         not a refusal and the drawer must not say nothing was submitted.
         UPLOAD_NO_RESPONSE_CODE is reserved for the upload request itself:
         an unanswered stamp is tagged as the stamp phase (httpStatus 0), so
         the learner is never told to send a saved upload again (PR2 fix
         round, F1). */
      return rejectWithValue(tagRejection(error, "stamp"));
    }
  },
);

const initialState = {
  cohort: null,
  capsules: [],
  milestones: [],
  // v4.0 Lesson Experience F1 (Codex v2 finding #4):
  // Explicit loading state for milestones so MissionPage can distinguish
  // idle / loading / loaded / error. An empty `milestones` array is a
  // valid loaded state for a capsule with zero authored milestones, so
  // length is not a load guard. Legacy code that reads `milestones`
  // directly continues to work - these fields are additive.
  milestonesStatus: "idle",
  milestonesError: null,
  point: 0,
  step: 0,
  // P1-2 (codex): take-latest guard for fetchProgramData. Records the latest
  // in-flight request's id so a slow response from a PRIOR session (e.g. user A
  // before user B logged in on the same tab) can't overwrite the newer store.
  // Behavior-preserving for the normal single-session flow: every fulfill IS the
  // latest request when it lands, so nothing changes.
  programRequestId: null,
  /* W4-F2.3 fix batch item 4 (Codex M1): the same take-latest guard for
     `fetchMilestones`, which had none. Three call sites dispatch it (the
     objective drawer, the sidebar, the mission page) and its fulfilled
     REPLACES `milestones` whole, so a slow earlier response landing after a
     newer one silently served last level's milestones to every consumer.
     Behaviour-preserving for the ordinary single-read flow, exactly as the
     neighbour above is: every fulfilment IS the latest when it lands. */
  milestonesRequestId: null,
  progressTitleLabel: [
    {
      id: 0,
      title: "challenge unlocked",
      label: "you know what you're building. let's make it real.",
    },
    {
      id: 1,
      title: "briefings finished",
      label: "briefings finished. bonus XP for correct answers.",
    },
    {
      id: 2,
      title: "your take is saved",
      label: "you connected the dots. that's how it sticks.",
    },
    {
      id: 3,
      title: "mission read",
      label: "you know what's ahead. now plan how to get there.",
    },
    {
      id: 4,
      title: "task created",
      label: "breaking it down is half the battle.",
    },
    {
      id: 5,
      title: "tasks complete",
      label: "the work is done. now show what you learned.",
    },
    {
      id: 6,
      title: "check-in submitted",
      label: "your read on how the squad is working is in.",
    },
    {
      id: 7,
      title: "work submitted",
      label: "your work is in. that's what this is all about.",
    },
  ],
};

const dashboardSlice = createSlice({
  name: "dashboard",
  initialState,
  reducers: {
    completedModule: (state, action) => {
      state.capsules = state.capsules.map((capsule) =>
        capsule.id === action.payload.capsule_id
          ? {
              ...capsule,
              modules: capsule.modules.map((module) =>
                module.id === action.payload.module_id
                  ? {
                      ...module,
                      is_completed: true,
                      needs_reflection: true,
                    }
                  : module,
              ),
            }
          : capsule,
      );
    },
    setPoint: (state, action) => {
      state.point = action.payload;
    },
    setStep: (state, action) => {
      state.step = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(completeReadChallenge.fulfilled, (state, action) => {
      state.cohort = action.payload;
    });
    builder.addCase(completeReadChallenge.rejected, (state, action) => {
      /* Kickoff refinement r6: an ABORTED dispatch is the Kickoff unmounting
         mid-commit, not a failure. Returning here is what lets the abort
         replace a late `fulfilled` without throwing inside dispatch. Nothing
         else aborts this thunk, so every other caller is unchanged. */
      if (action.meta.aborted) return;
      throw action.payload;
    });
    // P1-2: take-latest. Record the latest dispatch's requestId on pending;
    // apply the payload ONLY when this fulfilled is still the latest request.
    // RTK generates a unique requestId per dispatch, and pending fires
    // synchronously at dispatch time, so the last-dispatched request is always
    // the recorded one. A stale earlier response (a prior session's) is dropped.
    builder.addCase(fetchProgramData.pending, (state, action) => {
      state.programRequestId = action.meta.requestId;
    });
    builder.addCase(fetchProgramData.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.programRequestId) return;
      state.capsules = action.payload.capsules;
    });
    // A rejected never clears capsules (there is intentionally no capsule mutation
    // here), so a failed stale request can't wipe newer state. The guard is kept
    // explicit for symmetry / future-proofing.
    builder.addCase(fetchProgramData.rejected, (state, action) => {
      if (action.meta.requestId !== state.programRequestId) return;
      // no-op: capsules are left intact on failure (unchanged behavior)
    });
    builder.addCase(fetchMilestones.pending, (state, action) => {
      state.milestonesRequestId = action.meta.requestId;
      state.milestonesStatus = "loading";
      state.milestonesError = null;
    });
    builder.addCase(fetchMilestones.fulfilled, (state, action) => {
      // W4-F2.3 fix batch item 4 (Codex M1). See `milestonesRequestId`.
      if (action.meta.requestId !== state.milestonesRequestId) return;
      state.milestones = action.payload.program.capsules
        .map((capsule) => capsule.milestones)
        .flat();
      state.milestonesStatus = "loaded";
      state.milestonesError = null;
    });
    builder.addCase(fetchMilestones.rejected, (state, action) => {
      /* AND A STALE FAILURE DOES NOT MOVE THE STATUS EITHER. Without this a
         slow earlier read failing after a newer one succeeded would paint the
         error surface over milestones the page is holding and rendering. */
      if (action.meta.requestId !== state.milestonesRequestId) return;
      state.milestonesStatus = "error";
      state.milestonesError =
        action.error?.message || "Failed to load milestones";
    });
    builder.addCase(fetchCompleteLesson.fulfilled, (state, action) => {
      const { capsule_id, module_id, lesson_id, ...userLessonData } =
        action.payload;
      const capsule = state.capsules.find((c) => c.id === capsule_id);
      if (capsule) {
        const module = capsule.modules.find((m) => m.id === module_id);
        if (module) {
          const lesson = module.lessons.find((l) => l.id === lesson_id);
          if (lesson) {
            // Only increment completed_lessons if this lesson wasn't already completed
            const wasAlreadyCompleted = lesson.userLesson?.is_completed;
            lesson.userLesson = userLessonData;

            // Increment the completed_lessons count for progress bar update
            if (!wasAlreadyCompleted && userLessonData.is_completed) {
              module.completed_lessons = (module.completed_lessons || 0) + 1;
            }
          }
        }
      }
    });
    // F1: the replay increment answers with the authoritative row. Only
    // replay_count is taken from it - is_completed and the rest are already
    // true locally and re-assigning the whole row here would let a stale
    // field from a concurrent write land in the store.
    builder.addCase(fetchRecordLessonReplay.fulfilled, (state, action) => {
      const { capsule_id, module_id, lesson_id, replay_count } =
        action.payload || {};
      if (replay_count == null) return;
      const capsule = state.capsules.find((c) => c.id === capsule_id);
      const module = capsule?.modules?.find((m) => m.id === module_id);
      const lesson = module?.lessons?.find((l) => l.id === lesson_id);
      if (!lesson?.userLesson) return;
      // CodeRabbit MINOR (2026-09-13): TAKE THE MAXIMUM, never the last
      // response. Two increments can be in flight at once (two media ends in
      // quick succession), and nothing orders the responses. A plain
      // assignment lets the reply carrying n+1 land after the reply carrying
      // n+2 and restore the lower value - the store then reads a replay count
      // that went DOWN, which is the one thing the deployed contract says
      // cannot happen. The server counter is monotonic per user_lesson, so
      // the largest value seen is always the newest fact.
      const incoming = Number(replay_count);
      if (!Number.isFinite(incoming)) return;
      const held = lesson.userLesson.replay_count;
      const heldNum = held == null ? null : Number(held);
      lesson.userLesson.replay_count =
        heldNum != null && Number.isFinite(heldNum)
          ? Math.max(heldNum, incoming)
          : incoming;
    });
    builder.addCase(fetchUpdateNextLesson.fulfilled, (state, action) => {
      const { capsule_id, module_id, lesson_id, ...userLessonData } =
        action.payload;
      const capsule = state.capsules.find((c) => c.id === capsule_id);
      if (capsule) {
        const module = capsule.modules.find((m) => m.id === module_id);
        if (module) {
          const lesson = module.lessons.find((l) => l.id === lesson_id);
          if (lesson) {
            lesson.userLesson = userLessonData;
          }
        }
      }
    });
    builder.addCase(fetchReflection.fulfilled, (state, action) => {
      state.capsules = state.capsules.map((capsule) =>
        capsule.id === action.payload.module.capsule_id
          ? {
              ...capsule,
              modules: capsule.modules.map((module) =>
                module.id === action.payload.module_id
                  ? {
                      ...module,
                      reflection: action.payload.module.reflection,
                    }
                  : module,
              ),
            }
          : capsule,
      );
    });
  },
});

export const { completedModule, setPoint, setStep } = dashboardSlice.actions;

export default dashboardSlice.reducer;
